import { useState, useEffect, useMemo } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useNavigate } from "react-router-dom";
import { evaluateVisibility, getVisibleFields, getVisibleDocuments } from "utilities/schemaEval";
import { isClient } from "utilities/userType";
import { applicationPercentErrors, sectionPercentErrors } from "utilities/percentBounds";
import {
  resolveClientLandingPath,
  shouldLeaveRegistration,
  LANDING_DASHBOARD,
} from "utilities/clientLanding";

/**
 * @param {Object} schema - The application schema from useApplicationSchema()
 */
export const useRegistration = (schema) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [registrationData, setRegistrationData] = useState(null);
  // SOUND-777: the record as the server last had it. Range checks only BLOCK a
  // percentage the merchant changed — an out-of-range value already on record
  // (28 registrations hold e.g. -9) warns instead, mirroring sound-api's
  // enforceRangeOnChangedValues. Without this the wizard, which PUTs the whole
  // record, could never save those registrations at all.
  const [storedData, setStoredData] = useState(null);

  // SOUND-726. Three states, because "not loaded" and "loaded and leaving" are
  // different things and the page renders differently for each.
  //
  // Registration.jsx used to gate on `!registrationData` alone and return null,
  // so any load that never produced data — a 404, a transient error, a client
  // whose companyId had not resolved — rendered a permanently EMPTY page. Same
  // silent blank as RequireRole, on the same route.
  //   "loading"   — waiting on the fetch
  //   "ready"     — this merchant belongs on the wizard
  //   "leaving"   — resolved to somewhere else; a redirect is in flight
  //   "error"     — the fetch failed and we could not place them
  const [loadState, setLoadState] = useState("loading");
  const navigate = useNavigate();
  const auth = useAuth();

  // Derive step names and required field lists from the schema
  const STEPS = useMemo(() => {
    if (!schema) return [];
    return [
      ...schema.steps.map((s) => s.title),
      "Documents",
      "Review & Submit",
    ];
  }, [schema]);

  const requiredFieldsByStep = useMemo(() => {
    if (!schema) return {};
    const map = {};
    for (const step of schema.steps) {
      if (step.sectionType === "repeatable") {
        // For repeatable sections, required fields depend on item data + visibility
        map[step.key] = step.fields
          .filter((f) => f.required)
          .map((f) => f.name);
      } else {
        map[step.key] = step.fields
          .filter((f) => f.required)
          .map((f) => f.name);
      }
    }
    return map;
  }, [schema]);

  const requiredDocumentTypes = useMemo(() => {
    if (!schema || !registrationData) return [];
    const visibleDocs = getVisibleDocuments(schema.documents, registrationData);
    return visibleDocs.filter((d) => d.required).map((d) => d.type);
  }, [schema, registrationData]);

  // On load, fetch registration data
  useEffect(() => {
    const fetchRegistration = async () => {
      try {
        // Not resolved yet. RequireRole holds the route on a loader until
        // auth.user is a real object, so this is the brief window before the
        // merged profile lands rather than a dead end. Stay in "loading".
        const companyId = auth?.user?.companyId;
        if (!companyId) return;

        const response = await makeApiRequest(
          "get",
          apiRoutes.getApplication(auth.user.companyId),
          auth.user,
          auth.user.companyId,
          null
        );

        if (response.status === 200) {
          setRegistrationData(response.data);
          setStoredData(response.data);

          // SOUND-726: one rule for all six statuses, in one tested place.
          //
          // This was `["submitted", "approved"].includes(status)` with rejected
          // on a separate branch — four of the six the API defines. needs_info
          // fell through and happened to land correctly, which is not the same
          // as being handled. See utilities/clientLanding.js, which mirrors
          // REGISTRATION_CLIENT_EDITABLE_STATUSES in sound-api.
          const status = response.data.status;
          if (shouldLeaveRegistration(status)) {
            setLoadState("leaving");
            navigate(resolveClientLandingPath(status), { replace: true });
          } else {
            setLoadState("ready");
          }
        }
      } catch (error) {
        console.error("Failed to load registration data", error);

        // Parent company users have no application — redirect to dashboard
        if (error.response?.status === 404 && isClient(auth.user)) {
          try {
            const childRes = await makeApiRequest(
              "get",
              apiRoutes.getCompanyChildren(auth.user.companyId),
              auth.user,
              auth.user.companyId
            );
            if (childRes.data?.length > 0) {
              setLoadState("leaving");
              navigate(LANDING_DASHBOARD, { replace: true });
              return;
            }
          } catch {
            // Not a parent company — fall through to the error state below.
          }
        }

        // SOUND-726: say so.
        //
        // Every path out of this catch used to leave `registrationData` null,
        // and Registration.jsx rendered null on exactly that — so a failed load
        // produced a blank page that never resolved and never explained itself.
        // The wizard now renders a real message with a way out.
        setLoadState("error");
      }
    };

    fetchRegistration();
  }, [auth?.user?.companyId, navigate]);

  /**
   * Handle data changes.
   * For flat sections: onChange(section, field, value)
   * For repeatable sections (array replacement): onChange(section, null, newArray)
   */
  const handleDataChange = (section, field, value) => {
    setRegistrationData((prev) => {
      if (field === null) {
        // Wholesale array replacement (for repeatable sections)
        return { ...prev, [section]: value };
      }
      return {
        ...prev,
        [section]: {
          ...prev?.[section],
          [field]: value,
        },
      };
    });
  };

  const saveRegistration = async () => {
    try {
      const companyId = auth?.user?.companyId;

      if (!companyId || !registrationData) {
        return false;
      }

      // SOUND-777: a negative or >100 percentage is never saved — unless it is
      // the value already on record (it warns inline instead). The field
      // already shows its inline error, so refusing here is enough.
      if (applicationPercentErrors(schema, registrationData, storedData).length > 0) {
        console.error("Out-of-range percentage — not saving");
        return false;
      }

      const { _id, documents, submittedAt, ...cleanedData } = registrationData;

      await makeApiRequest(
        "put",
        apiRoutes.updateApplication(companyId),
        auth.user,
        companyId,
        cleanedData
      );
      // What we just wrote is now what the server holds.
      setStoredData(registrationData);
      return true;
    } catch (error) {
      console.error("Failed to save registration update", error);
      return false;
    }
  };

  const handleNext = async () => {
    const saved = await saveRegistration();

    if (!saved) {
      console.error("Save failed — not advancing to next step");
      return;
    }

    if (currentStep < STEPS.length) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = async () => {
    const saved = await saveRegistration();

    if (!saved) {
      console.error("Save failed — not going back");
      return;
    }

    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleDocumentUpload = async (files, type) => {
    try {
      const companyId = auth?.user?.companyId;
      if (!companyId || !files) return;

      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      formData.append("documentType", type);

      const response = await makeApiRequest(
        "post",
        apiRoutes.uploadApplicationFile(companyId),
        auth.user,
        companyId,
        formData,
        true
      );

      if (response?.status === 201) {
        const { documentType, files: uploadedFiles } = response.data.data;

        setRegistrationData((prev) => {
          const updated = { ...prev };
          const docs = updated.documents || [];

          const index = docs.findIndex((d) => d.type === documentType);
          if (index > -1) {
            updated.documents[index].files = [
              ...updated.documents[index].files,
              ...uploadedFiles,
            ];
          } else {
            updated.documents.push({
              type: documentType,
              files: uploadedFiles,
            });
          }

          return updated;
        });
      }
    } catch (error) {
      console.error("Document upload failed", error);
    }
  };

  const handleDocumentDelete = async (fileKey, type) => {
    const companyId = auth?.user?.companyId;
    if (!companyId || !fileKey || !type) return;

    const url =
      apiRoutes.deleteApplicationFile(companyId) +
      `?documentType=${encodeURIComponent(type)}&key=${encodeURIComponent(
        fileKey
      )}`;

    try {
      const response = await makeApiRequest(
        "delete",
        url,
        auth.user,
        companyId
      );

      if (response?.status === 200) {
        setRegistrationData((prev) => {
          const updated = { ...prev };
          const docs = updated.documents || [];

          const index = docs.findIndex((d) => d.type === type);
          if (index > -1) {
            updated.documents[index].files = docs[index].files.filter(
              (f) => f.key !== fileKey
            );

            // Remove type entirely if no files remain
            if (updated.documents[index].files.length === 0) {
              updated.documents.splice(index, 1);
            }
          }

          return updated;
        });
      }
    } catch (error) {
      console.error("Error during document deletion", error);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await makeApiRequest(
        "put",
        apiRoutes.submitApplication(auth.user.companyId),
        auth.user,
        auth.user.companyId,
        null
      );

      if (response?.status === 200) {
        navigate("/registration-confirmation");
      }
    } catch (error) {
      console.error("Submit failed", error);
    }
  };

  const canProceed = () => {
    if (!schema || !registrationData) return false;

    const stepIndex = currentStep - 1;

    // Field steps (1 through N where N = schema.steps.length)
    if (stepIndex < schema.steps.length) {
      const step = schema.steps[stepIndex];

      if (step.sectionType === "repeatable") {
        // Repeatable section — check each item's visible required fields
        const items = registrationData[step.arrayField] || [];
        if (items.length === 0) return false;

        const nonSignerItems = step.arrayField === "owners"
          ? items.filter((i) => i.ownerType !== "signer")
          : items;

        const allFilled = nonSignerItems.every((item) => {
          const visibleFields = getVisibleFields(step.fields, item, registrationData);
          const visibleRequired = visibleFields.filter((f) => f.required);
          return visibleRequired.every((f) => {
            const val = item[f.name];
            return val !== undefined && val !== null && String(val).trim() !== "";
          });
        });
        if (!allFilled) return false;

        // Owners must total exactly 100% ownership
        if (step.arrayField === "owners") {
          const total = nonSignerItems.reduce((sum, o) => {
            const pct = Number(o.ownershipPercentage);
            return sum + (isNaN(pct) ? 0 : pct);
          }, 0);
          if (total !== 100) return false;
        }

        return true;
      }

      // Flat section — check visible required fields
      const section = registrationData[step.key] || {};
      const visibleFields = getVisibleFields(step.fields, section, registrationData);
      const visibleRequired = visibleFields.filter((f) => f.required);

      // SOUND-777: out-of-range percentages block Next like a missing field.
      const storedSection = storedData ? storedData[step.key] || {} : undefined;
      if (Object.keys(sectionPercentErrors(visibleFields, section, storedSection)).length > 0) {
        return false;
      }

      return visibleRequired.every((field) => {
        const val = section[field.name];
        return val !== undefined && val !== null && String(val).trim() !== "";
      });
    }

    // Documents step
    if (stepIndex === schema.steps.length) {
      const documents = registrationData.documents || [];
      return requiredDocumentTypes.every((type) => {
        const entry = documents.find((d) => d.type === type);
        return entry?.files?.length > 0;
      });
    }

    // Review step
    return true;
  };

  return {
    currentStep,
    registrationData,
    storedData,
    loadState,
    STEPS,
    handleDataChange,
    handleNext,
    handlePrevious,
    handleSubmit,
    handleDocumentUpload,
    handleDocumentDelete,
    canProceed,
  };
};
